import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { Camera, Loader2, Send } from "lucide-react";

interface FeedbackModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const FeedbackModal = ({ open, onOpenChange }: FeedbackModalProps) => {
  const { user } = useAuth();
  const [type, setType] = useState<"suggestion" | "bug" | "autre">("suggestion");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState(user?.email || "");
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleScreenshot = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      toast.error("Format invalide", {
        description: "Seuls les fichiers PNG et JPG sont acceptés"
      });
      e.target.value = '';
      return;
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      toast.error("Fichier trop volumineux", {
        description: "La taille maximale est de 5MB"
      });
      e.target.value = '';
      return;
    }

    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setScreenshotPreview(previewUrl);
    setScreenshot(file);
  };

  const removeScreenshot = () => {
    if (screenshotPreview) {
      URL.revokeObjectURL(screenshotPreview);
    }
    setScreenshot(null);
    setScreenshotPreview(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!message.trim() || !email.trim()) {
      toast.error("Erreur", {
        description: "Veuillez remplir tous les champs obligatoires"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Upload screenshot if provided (bucket is private; edge function generates signed URL)
      let screenshotPath: string | null = null;
      if (screenshot) {
        if (!user) {
          toast.error("Connexion requise", {
            description: "Vous devez être connecté pour joindre une capture d'écran."
          });
          setIsSubmitting(false);
          return;
        }
        const fileExt = (screenshot.name.split('.').pop() || 'png').toLowerCase();
        const fileName = `${user.id}/${Math.random().toString(36).substring(2)}_${Date.now()}.${fileExt}`;

        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('feedback-screenshots')
          .upload(fileName, screenshot);

        if (uploadError) throw uploadError;
        screenshotPath = uploadData.path;
      }

      // Call edge function to send email and store in DB
      const { error } = await supabase.functions.invoke('send-feedback', {
        body: {
          type,
          message,
          email,
          pageUrl: window.location.href,
          screenshotPath,
        }
      });

      if (error) throw error;

      toast.success("Merci pour votre retour !", {
        description: "Votre message a été envoyé avec succès."
      });

      // Reset form
      setMessage("");
      setType("suggestion");
      removeScreenshot();
      onOpenChange(false);
    } catch (error) {
      console.error("Error submitting feedback:", error);
      toast.error("Erreur", {
        description: "Impossible d'envoyer votre feedback. Veuillez réessayer."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Envoyer un feedback</DialogTitle>
          <DialogDescription>
            Partagez vos suggestions ou signalez un bug pour nous aider à améliorer PartXplorer.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label>Type de feedback *</Label>
            <RadioGroup value={type} onValueChange={(v) => setType(v as any)}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="suggestion" id="suggestion" />
                <Label htmlFor="suggestion" className="font-normal cursor-pointer">
                  💡 Suggestion d'amélioration
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="bug" id="bug" />
                <Label htmlFor="bug" className="font-normal cursor-pointer">
                  🐛 Signaler un bug
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="autre" id="autre" />
                <Label htmlFor="autre" className="font-normal cursor-pointer">
                  📝 Autre
                </Label>
              </div>
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">Message *</Label>
            <Textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Décrivez votre suggestion ou le bug rencontré..."
              className="min-h-[120px]"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="votre@email.com"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="screenshot">Capture d'écran (optionnel)</Label>
            <div className="flex items-center gap-2">
              <Input
                id="screenshot"
                type="file"
                accept="image/png,image/jpeg,image/jpg"
                onChange={handleScreenshot}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => document.getElementById('screenshot')?.click()}
                className="w-full"
              >
                <Camera className="h-4 w-4 mr-2" />
                {screenshot ? screenshot.name : "Ajouter une capture"}
              </Button>
            </div>
            {screenshotPreview && (
              <div className="relative mt-2 border rounded-lg overflow-hidden">
                <img 
                  src={screenshotPreview} 
                  alt="Aperçu" 
                  className="w-full h-auto max-h-48 object-contain bg-muted"
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={removeScreenshot}
                  className="absolute top-2 right-2"
                >
                  Supprimer
                </Button>
              </div>
            )}
          </div>

          <div className="flex gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
              disabled={isSubmitting}
            >
              Annuler
            </Button>
            <Button type="submit" className="flex-1" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Envoi...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Envoyer
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackModal;
